// Pure decision helpers for segment-palette (re)generation.
//
// Kept dependency-free (no React, no redux, no asset imports) so the
// no-write-storm guard from PR #1711 and the deliberate manual-Regenerate
// bypass (PAL-002) are unit-testable in isolation — and so importing them never
// drags in the builder's heavy module graph.
//
// Claude · segment-palette-regenerate · 2026-06-13

/**
 * Returns the base color a segment's existing palette was generated from, or
 * null if the segment has no usable palette yet. The base color is the first
 * (locked) color of the palette; we also accept the explicit `color`/metadata
 * fields the palette carries. Comparison is case-insensitive on the hex so a
 * "#AABBCC" vs "#aabbcc" mismatch never forces a needless regenerate.
 */
export const getExistingPaletteBaseColor = (segment) => {
  const palette =
    segment?.settings?.paletteData || segment?.settings?.palette_data;
  if (!palette) return null;
  const base =
    palette.color ||
    palette.metadata?.color ||
    palette.colors?.[0]?.hex ||
    null;
  return base ? String(base).toLowerCase() : null;
};

/**
 * Pure decision for whether fetchSegmentPalette should fire a fresh AI
 * generate-scheme + save, or reuse the segment's existing palette. Extracted so
 * the no-write-storm guard (the heart of #1711) and the deliberate-reroll
 * bypass are unit-testable without React.
 *
 *  - force=true (the explicit manual Regenerate, PAL-002): ALWAYS regenerate,
 *    even for an unchanged color. This is the sanctioned variety reroll.
 *  - guardEnabled=false (flag killed): legacy always-regenerate behaviour.
 *  - otherwise: regenerate ONLY when the segment has no palette for this exact
 *    color (i.e. the color actually changed). Reuse — and thus NO paid AI call —
 *    when the stored base color already matches.
 *
 * @returns {boolean} true if a fresh palette should be generated.
 */
export const shouldRegeneratePalette = (
  segment,
  color,
  { force = false, guardEnabled = true } = {}
) => {
  if (force) return true;
  if (!guardEnabled) return true;
  const existingBase = getExistingPaletteBaseColor(segment);
  if (existingBase && existingBase === String(color).toLowerCase()) {
    return false;
  }
  return true;
};

/**
 * Pure decision for whether fetchSegmentPaletteFromFill should fire a fresh
 * derive, for the FILL path (image/gif/gradient/pattern) — Gordon 2026-09-06
 * verbatim: "GIFs and Loops and any motion segments — the motion cannot
 * effect the palette once it is generated, it is locked until the wedge fill
 * is replaced."
 *
 * A fill-derived palette records the exact seed signature (`fillSeedSig`,
 * e.g. an image/gif url, or the joined ordered gradient/pattern colors) it was
 * generated from. As long as the segment's CURRENT seed signature still
 * matches, re-deriving is a no-op — so a GIF's frame/motion advancing (same
 * url, same signature) can never re-fire the (paid) extractor. Replacing the
 * wedge fill changes the signature, which unlocks exactly one fresh derive.
 *
 *  - force=true (a future explicit manual Regenerate): ALWAYS regenerate.
 *  - no existing fill-derived palette, or a different signature: regenerate.
 *  - same signature: false (locked, no-op).
 *
 * @returns {boolean} true if a fresh fill-derive should be fired.
 */
export const shouldRegenerateFillPalette = (
  segment,
  seedSig,
  { force = false } = {}
) => {
  if (force) return true;
  const palette = segment?.settings?.paletteData;
  if (!palette || palette.source !== "fill") return true;
  const existingSig = palette.fillSeedSig;
  if (existingSig == null) return true;
  return existingSig !== seedSig;
};
